import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import { dropPushSubscription } from "../lib/pushLifecycle";

const API_URL = process.env.REACT_APP_API_URL || "";

/** Sentinel the Settings page maps to the translated browser-support toast. */
export const NOT_SUPPORTED_ERROR = "push_not_supported_browser";

/**
 * `subscribe` returns a result rather than a bare boolean since ticket 12: the
 * caller has to distinguish "this browser's push service is not one we send to"
 * (a clear, actionable message) from a permission refusal or a transient
 * failure, and reading the `error` state right after an await would read the
 * pre-render value.
 */
export type PushSubscribeResult =
  | { ok: true }
  | { ok: false; reason: "not_supported_browser" | "permission" | "error" };

interface PushContextType {
  isPushSupported: boolean;
  isPushEnabled: boolean;
  permissionState: NotificationPermission | "loading";
  isSubscribing: boolean;
  error: string | null;
  requestPermission: () => Promise<boolean>;
  subscribe: () => Promise<PushSubscribeResult>;
  unsubscribe: () => Promise<boolean>;
}

const PushContext = createContext<PushContextType | null>(null);

/**
 * Convert a base64 string to Uint8Array for VAPID key
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function PushProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, entityType, entityId } = useAuth();
  const navigate = useNavigate();

  // Bumped on every sync run, so a response that lands after an entity switch
  // cannot write state for the principal it was issued for.
  const epochRef = useRef(0);

  const [isPushSupported] = useState(() => {
    return "serviceWorker" in navigator && "PushManager" in window;
  });
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [permissionState, setPermissionState] = useState<NotificationPermission | "loading">("loading");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);

  // Register service worker on mount
  useEffect(() => {
    if (!isPushSupported) {
      setPermissionState("denied");
      return;
    }

    const registerServiceWorker = async () => {
      try {
        const registration = await navigator.serviceWorker.register("/sw.js", {
          scope: "/",
        });
        console.log("[Push] Service Worker registered:", registration.scope);
        setSwRegistration(registration);

        // Check current subscription status
        const subscription = await registration.pushManager.getSubscription();
        setIsPushEnabled(!!subscription);

        // Update permission state
        setPermissionState(Notification.permission);
      } catch (err) {
        console.error("[Push] Service Worker registration failed:", err);
        setError("Failed to register service worker");
        setPermissionState("denied");
      }
    };

    registerServiceWorker();
  }, [isPushSupported]);

  // Listen for messages from service worker
  useEffect(() => {
    if (!isPushSupported) return;

    const handleMessage = (event: MessageEvent) => {
      console.log("[Push] Message from SW:", event.data);

      if (event.data.type === "REFRESH_PAGE") {
        window.location.reload();
      }

      if (event.data.type === "NAVIGATE" && event.data.url) {
        navigate(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [isPushSupported, navigate]);

  // Request notification permission
  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!isPushSupported) {
      setError("Push notifications are not supported");
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      return permission === "granted";
    } catch (err) {
      console.error("[Push] Permission request failed:", err);
      setError("Failed to request permission");
      return false;
    }
  }, [isPushSupported]);

  // Subscribe to push notifications
  const subscribe = useCallback(async (): Promise<PushSubscribeResult> => {
    if (!isPushSupported || !swRegistration || !isAuthenticated) {
      return { ok: false, reason: "error" };
    }

    setIsSubscribing(true);
    setError(null);

    try {
      // Request permission if not granted
      if (Notification.permission !== "granted") {
        const granted = await requestPermission();
        if (!granted) {
          setIsSubscribing(false);
          return { ok: false, reason: "permission" };
        }
      }

      // Get VAPID public key from server
      const vapidResponse = await axios.get(`${API_URL}/api/push/vapid-key`);
      if (!vapidResponse.data.success) {
        throw new Error("Push notifications not configured on server");
      }

      const vapidPublicKey = vapidResponse.data.data.publicKey;

      // Subscribe to push notifications
      const subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      console.log("[Push] Subscription created:", subscription);

      // Send subscription to server
      const subscriptionJson = subscription.toJSON();
      const response = await axios.post(
        `${API_URL}/api/push/subscribe`,
        {
          subscription: {
            endpoint: subscriptionJson.endpoint,
            keys: {
              p256dh: subscriptionJson.keys?.p256dh,
              auth: subscriptionJson.keys?.auth,
            },
          },
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        setIsPushEnabled(true);
        console.log("[Push] Subscription saved to server");
        return { ok: true };
      } else {
        throw new Error(response.data.error || "Failed to save subscription");
      }
    } catch (err: any) {
      console.error("[Push] Subscribe failed:", err);
      // Ticket 12's admission gate: an unrecognised push service answers 400
      // with a machine code. Surface it as the plain browser-support message
      // rather than the raw code (spec "UI / behavior").
      const code = err.response?.data?.code;
      if (code === "push_endpoint_not_allowed" || code === "push_keys_invalid") {
        setError(NOT_SUPPORTED_ERROR);
        return { ok: false, reason: "not_supported_browser" };
      }
      setError(err.message || "Failed to subscribe");
      return { ok: false, reason: "error" };
    } finally {
      setIsSubscribing(false);
    }
  }, [isPushSupported, swRegistration, isAuthenticated, requestPermission]);

  // Unsubscribe from push notifications. Delegates to the shared helper so
  // this and AuthContext.logout have one implementation (ticket 12, spec §E).
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!swRegistration) {
      return false;
    }

    await dropPushSubscription({ notifyServer: true });
    setIsPushEnabled(false);
    console.log("[Push] Unsubscribed successfully");
    return true;
  }, [swRegistration]);

  // Sync subscription state with server when authenticated.
  //
  // `entityType`/`entityId` are in the deps since ticket 12: without them the
  // effect did not re-run on an entity switch, so the device kept its previous
  // binding for the rest of the browser session (spec §E).
  //
  // The epoch guard covers the `setIsPushEnabled` writes only. The *binding*
  // cannot invert — the server resolves the principal from the session at
  // arrival time, never from anything the client names (spec decision 9) — but
  // a response that lands after a switch could still write a state flag for a
  // principal that is gone.
  useEffect(() => {
    if (
      isAuthenticated &&
      isPushSupported &&
      swRegistration &&
      Notification.permission === "granted"
    ) {
      const epoch = ++epochRef.current;
      const isCurrent = () => epoch === epochRef.current;

      // Check if we have an existing subscription and sync with server
      swRegistration.pushManager.getSubscription().then(async (subscription) => {
        if (subscription) {
          // Verify and re-register with server (in case it was lost)
          try {
            const subscriptionJson = subscription.toJSON();
            const response = await axios.post(
              `${API_URL}/api/push/subscribe`,
              {
                subscription: {
                  endpoint: subscriptionJson.endpoint,
                  keys: {
                    p256dh: subscriptionJson.keys?.p256dh,
                    auth: subscriptionJson.keys?.auth,
                  },
                },
              },
              { withCredentials: true }
            );
            if (response.data.success && isCurrent()) {
              setIsPushEnabled(true);
              console.log("[Push] Subscription synced with server");
            }
          } catch (err) {
            console.error("[Push] Failed to sync subscription:", err);
            // Subscription might be invalid, mark as disabled
            if (isCurrent()) setIsPushEnabled(false);
          }
        } else if (isCurrent()) {
          setIsPushEnabled(false);
        }
      });
    }
  }, [isAuthenticated, isPushSupported, swRegistration, entityType, entityId]);

  return (
    <PushContext.Provider
      value={{
        isPushSupported,
        isPushEnabled,
        permissionState,
        isSubscribing,
        error,
        requestPermission,
        subscribe,
        unsubscribe,
      }}
    >
      {children}
    </PushContext.Provider>
  );
}

export function usePush() {
  const context = useContext(PushContext);
  if (!context) {
    throw new Error("usePush must be used within a PushProvider");
  }
  return context;
}
